/***************************************************************************/
"use client";
/***************************************************************************/

import { useFetch } from "@/client/api/use-fetch";
import { TError, TResponseBody, TValidationError } from "@/universal/api/types";
import { useState } from "react";
import { TFetchResponse } from "./types";

/**
 * Type representing the state of the data mutation operation.
 * @template TResponseData - The expected response data type.
 * @template TValidationErrorField - The type of validation error field.
 * @property {boolean} loading - Indicates if the request is in progress.
 * @property {TResponseData | undefined} data - The data returned by a successful request.
 * @property {TError | undefined} error - Error information if the request fails.
 * @property {TValidationError<TValidationErrorField> | undefined} validationErrors - Field-specific validation errors.
 * @property {"success" | "error" | undefined} status - The status of the request, either "success" or "error".
 * @property {number | undefined} statusCode - The HTTP status code from the response.
 * @property {string | undefined} statusText - The HTTP status text from the response.
 */
type TState<TResponseData, TValidationErrorField extends string = string> = {
  loading: boolean;
  data: TResponseData | undefined;
  error: TError;
  validationErrors: TValidationError<TValidationErrorField> | undefined;
  status: "success" | "error" | undefined;
  statusCode: number | undefined;
  statusText: string | undefined;
};

/**
 * Type representing the function to perform a mutation operation (POST, PATCH, PUT, DELETE).
 * @template TRequestData - The request data type.
 * @template TResponseData - The response data type.
 * @template TValidationErrorField - The type of validation error field.
 * @param {string} url - The endpoint to send the request to.
 * @param {TRequestData} requestData - The data to be sent in the request body.
 * @param {Omit<RequestInit, "method" | "body">} [options] - Additional fetch options.
 * @param {{ timeout?: number; key?: string }} [config] - Configurations for timeout and request key.
 * @returns {Promise<TState<TResponseData, TValidationErrorField>>} The state of the request without the loading indicator.
 */
type TOnMutate<
  TRequestData,
  TResponseData,
  TValidationErrorField extends string = string,
> = (
  url: string,
  requestData: TRequestData,
  options?: Omit<RequestInit, "method" | "body"> | undefined,
  config?: { timeout?: number; key?: string },
) => Promise<TState<TResponseData, TValidationErrorField>>;

/**
 * A hook to handle data mutations (POST, PATCH, PUT, DELETE) with loading, error, and status tracking.
 * Manages state and provides functions for each mutation method.
 * @template TRequestData - The request data type.
 * @template TResponseData - The expected response data type.
 * @template TValidationErrorField - The type of validation error field.
 * @returns {[TState, Object]} An array containing:
 *  1. The mutation state object (loading, data, error, status, statusCode, statusText).
 *  2. An object with mutation methods (post, patch, put, delete).
 */
const useMutate = <
  TRequestData,
  TResponseData,
  TValidationErrorField extends string = string,
>(): [
  TState<TResponseData, TValidationErrorField>,
  {
    post: TOnMutate<TRequestData, TResponseData, TValidationErrorField>;
    patch: TOnMutate<TRequestData, TResponseData, TValidationErrorField>;
    put: TOnMutate<TRequestData, TResponseData, TValidationErrorField>;
    delete: TOnMutate<TRequestData, TResponseData, TValidationErrorField>;
  },
] => {
  const [state, setState] = useState<
    TState<TResponseData, TValidationErrorField>
  >({
    loading: false,
    data: undefined,
    error: undefined,
    validationErrors: undefined,
    status: undefined,
    statusCode: undefined,
    statusText: undefined,
  });

  const fetcher = useFetch();

  /**
   * Function to initiate a mutation request (POST, PATCH, PUT, DELETE) and handle the response.
   * @param {"POST" | "PATCH" | "PUT" | "DELETE"} method - The HTTP method to use for the request.
   * @param {TRequestData} requestData - The data to be sent in the request body.
   * @param {Omit<RequestInit, "method" | "body">} [options] - Additional fetch options.
   * @param {{ timeout?: number; key?: string }} [config] - Configurations for timeout and request key.
   * @returns {Function} A function to perform the mutation request and update state.
   */
  const mutator =
    (method: "POST" | "PATCH" | "PUT" | "DELETE") =>
    async (
      url: string,
      requestData: TRequestData,
      options?: Omit<RequestInit, "method" | "body"> | undefined,
      config?: { timeout?: number; key?: string },
    ): Promise<TState<TResponseData, TValidationErrorField>> => {
      if (!state.loading) {
        setState({
          loading: true,
          data: undefined,
          error: undefined,
          validationErrors: undefined,
          status: undefined,
          statusCode: undefined,
          statusText: undefined,
        });

        const fn =
          method === "POST"
            ? fetcher.post
            : method === "PATCH"
              ? fetcher.patch
              : method === "PUT"
                ? fetcher.put
                : method === "DELETE"
                  ? fetcher.delete
                  : fetcher.post;

        const response = await fn<TRequestData, TResponseBody<TResponseData>>(
          url,
          requestData,
          options,
          config,
        )
          .then((response) => response)
          .catch(
            (response) =>
              response as TFetchResponse<
                TResponseBody<TResponseData> | undefined
              >,
          );

        const updatedState: TState<TResponseData, TValidationErrorField> = {
          loading: false,
          data: response.data?.data,
          error: response.data?.error,
          validationErrors: response.data?.validationErrors,
          status: response.ok ? "success" : "error",
          statusCode: response.status,
          statusText: response.statusText,
        };

        setState(updatedState);

        return updatedState;
      }

      return state;
    };

  return [
    state,
    {
      post: mutator("POST"),
      patch: mutator("PATCH"),
      put: mutator("PUT"),
      delete: mutator("DELETE"),
    },
  ];
};

export { useMutate };
